(() => {
  const DISCOUNT_PREFIX = 'PRIVATE';

  function moneyFormatter() {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    });
  }

  function isUnlocked(storageKey, accessHash) {
    if (!storageKey || !accessHash) return false;
    try {
      return localStorage.getItem(storageKey) === accessHash;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  function renderUnitPrice(container, formatter) {
    if (container.dataset.privateSaleRendered === 'true') return;

    const privateSalePrice = Number(container.dataset.privateSalePrice || 0);
    const originalPrice = Number(container.dataset.privateSaleOriginalPrice || 0);
    const storageKey = container.dataset.privateSaleStorageKey;
    const accessHash = container.dataset.privateSaleAccessHash;

    if (!privateSalePrice || !originalPrice || privateSalePrice >= originalPrice) return;
    if (!isUnlocked(storageKey, accessHash)) return;

    container.innerHTML = `
      <span class="visually-hidden">Regular price</span>
      <s class="cart-item__old-price product-option">${formatter.format(originalPrice / 100)}</s>
      <span class="visually-hidden">Sale price</span>
      <strong class="cart-item__final-price product-option">${formatter.format(privateSalePrice / 100)}</strong>
    `;
    container.dataset.privateSaleRendered = 'true';
  }

  function renderLinePrice(container, formatter) {
    if (container.dataset.privateSaleRendered === 'true') return;

    const privateSalePrice = Number(container.dataset.privateSalePrice || 0);
    const originalLinePrice = Number(container.dataset.privateSaleOriginalLinePrice || 0);
    const quantity = Number(container.dataset.privateSaleQuantity || 1);
    const storageKey = container.dataset.privateSaleStorageKey;
    const accessHash = container.dataset.privateSaleAccessHash;

    if (!privateSalePrice || !originalLinePrice) return;
    if (!isUnlocked(storageKey, accessHash)) return;

    const privateSaleLinePrice = privateSalePrice * quantity;
    if (privateSaleLinePrice >= originalLinePrice) return;

    container.innerHTML = `
      <div class="cart-item__discounted-prices">
        <span class="visually-hidden">Regular price</span>
        <s class="cart-item__old-price price price--end">${formatter.format(originalLinePrice / 100)}</s>
        <span class="visually-hidden">Sale price</span>
        <span class="price price--end">${formatter.format(privateSaleLinePrice / 100)}</span>
      </div>
    `;
    container.dataset.privateSaleRendered = 'true';
  }

  function applyPrivateSaleCartPrices(root = document) {
    const formatter = moneyFormatter();
    root.querySelectorAll('[data-private-sale-unit-price-container]').forEach((container) => {
      renderUnitPrice(container, formatter);
    });
    root.querySelectorAll('[data-private-sale-line-price-container]').forEach((container) => {
      renderLinePrice(container, formatter);
    });
  }

  function getDesiredPrivateSaleCodes(root = document) {
    const codes = new Set();

    root.querySelectorAll('[data-private-sale-cart-item]').forEach((node) => {
      const discountPercent = Number(node.dataset.privateSaleDiscountPercent || 0);
      const storageKey = node.dataset.privateSaleStorageKey;
      const accessHash = node.dataset.privateSaleAccessHash;

      if (!discountPercent || !isUnlocked(storageKey, accessHash)) return;
      codes.add(`${DISCOUNT_PREFIX}${discountPercent}`);
    });

    return Array.from(codes).sort((a, b) => a.localeCompare(b));
  }

  function getSectionsForDiscountRefresh() {
    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    const cartDrawer = document.getElementById('CartDrawer');

    if (cartDrawer) {
      sections.push({ id: 'cart-drawer', selector: '#CartDrawer', innerSelector: '.drawer__inner' });
      sections.push({ id: 'cart-icon-bubble', selector: '#cart-icon-bubble', innerSelector: '.shopify-section' });
    }

    if (mainCartItems?.dataset.id) {
      sections.push({ id: mainCartItems.dataset.id, selector: '#main-cart-items', innerSelector: '.js-contents' });
      sections.push({ id: 'cart-live-region-text', selector: '#cart-live-region-text', innerSelector: '.shopify-section' });
    }

    if (mainCartFooter?.dataset.id) {
      sections.push({ id: mainCartFooter.dataset.id, selector: '#main-cart-footer', innerSelector: '.js-contents' });
    }

    return sections;
  }

  function replaceSectionHtml(id, html, selector, innerSelector) {
    if (!html || !selector) return;

    const mount = document.querySelector(selector);
    if (!mount) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');

    if (innerSelector) {
      const currentTarget = mount.querySelector(innerSelector);
      const replacementTarget = parsed.querySelector(innerSelector);

      if (currentTarget && replacementTarget) {
        currentTarget.innerHTML = replacementTarget.innerHTML;
      }
      return;
    }

    const replacement = parsed.querySelector(selector) || parsed.querySelector('.shopify-section');
    if (!replacement) return;

    mount.innerHTML = replacement.innerHTML;
  }

  async function applyPrivateSaleDiscountCodes(root = document) {
    const desiredCodes = getDesiredPrivateSaleCodes(root);
    const desiredString = desiredCodes.join(',');

    if (!desiredString) return;
    if (window.__privateSaleDiscountUpdateInFlight) return;
    if (window.__privateSaleAppliedCodes === desiredString) return;

    window.__privateSaleDiscountUpdateInFlight = true;

    try {
      const sections = getSectionsForDiscountRefresh();
      const body = {
        discount: desiredString,
        sections: sections.map((section) => section.id),
        sections_url: window.location.pathname
      };

      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const parsed = await response.json();

      if (!response.ok) {
        console.error('Private sale discount update failed', parsed);
        return;
      }

      window.__privateSaleAppliedCodes = desiredString;

      if (parsed.sections) {
        sections.forEach((section) => {
          replaceSectionHtml(section.id, parsed.sections[section.id], section.selector, section.innerSelector);
        });
      }

      if (typeof window.updateProgressBar === 'function' && parsed.total_price !== undefined) {
        window.updateProgressBar(parsed.total_price);
      }

      applyPrivateSaleCartPrices(document);

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'private-sale-cart' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      window.__privateSaleDiscountUpdateInFlight = false;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    applyPrivateSaleCartPrices();
    applyPrivateSaleDiscountCodes();

    const observer = new MutationObserver(() => {
      applyPrivateSaleCartPrices();
      applyPrivateSaleDiscountCodes();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
})();
